import type { ReactNode, RefObject } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { cn } from '@/lib/utils'
import { useReducedMotion } from '@/lib/preferences'

export type HeroCoordinates = {
    /** Right ascension in hours, 0 to 24. */
    ra: number
    /** Declination in degrees, -90 to 90. */
    dec: number
}

type ProgressListener = (progress: number) => void

export type HeroContextValue = {
    /** Reduce-motion users get one composed still frame instead of the animation. */
    reduced: boolean
    /** True while the hero is a pinned, scroll-driven scene. */
    pinned: boolean
    /** Pinned scroll progress, 0 at the top of the hero to 1 when it releases. Always 0 when not pinned. */
    getProgress: () => number
    /** Calls the listener with the progress on every scroll frame (and once straight away). */
    subscribe: (listener: ProgressListener) => () => void
    /** Updates the RA and DEC in the corner label without re-rendering anything. */
    setCoordinates: (coordinates: HeroCoordinates | null) => void
    /** Jumps past the hero through a blink of black, then focuses the content after it. */
    skip: () => void
    /** Smoothly scrolls the pinned scene to a given progress. */
    scrollToProgress: (progress: number) => void
    /** True once the visitor pressed "Skip intro": scenes jump to their settled state. */
    skipped: boolean
    sectionRef: RefObject<HTMLElement | null>
    stickyRef: RefObject<HTMLDivElement | null>
}

const HeroContext = createContext<HeroContextValue | null>(null)

export const useHero = (): HeroContextValue => {
    const value = useContext(HeroContext)

    if (!value) throw new Error('useHero must be used inside <HeroFrame>.')

    return value
}

/** Runs the listener on every scroll frame of the pinned hero. Keep it cheap and write to refs, not state. */
export const useHeroProgress = (listener: ProgressListener): void => {
    const { subscribe } = useHero()
    const listenerRef = useRef(listener)

    listenerRef.current = listener

    useEffect(() => subscribe(progress => listenerRef.current(progress)), [subscribe])
}

const pad = (value: number): string => String(value).padStart(2, '0')

export const formatHeroRa = (hours: number): string => {
    const value = ((hours % 24) + 24) % 24
    let h = Math.floor(value)
    let m = Math.round((value - h) * 60)

    if (m === 60) {
        m = 0
        h = (h + 1) % 24
    }

    return `RA ${pad(h)}h ${pad(m)}m`
}

export const formatHeroDec = (degrees: number): string => `DEC ${degrees < 0 ? '−' : '+'}${Math.round(Math.abs(degrees))}°`

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value))

const BLINK_MS = 380

interface HeroFrameProps {
    /** Stable kebab-case id for the hero section. */
    id: string
    /** The page name shown first in the corner label, e.g. "Sky Tonight". */
    label: string
    /** The coordinates shown in the corner label until a scene updates them. */
    coordinates?: HeroCoordinates | null
    /** Extra viewport heights of pinned scrolling. 0 makes a single-screen hero. */
    screens?: number
    /** The id of the first element after the hero; "Skip intro" lands there. */
    contentId: string
    /** The id of the hero's visible title, for the section's accessible name. */
    titleId?: string
    /** Draws the thin progress scale on the right edge of pinned heroes. */
    showScale?: boolean
    /** Marks the viewport as a sky scene so the reticle cursor can read real coordinates. */
    sky?: boolean
    onSkip?: () => void
    className?: string
    children: ReactNode
}

/**
 * The shared frame every inner-page hero sits in: a full-screen stage, viewfinder corners,
 * a tiny mono label with live coordinates, a "Skip intro" link and, when pinned, a progress scale.
 */
export const HeroFrame = ({
    id,
    label,
    coordinates = null,
    screens = 0,
    contentId,
    titleId,
    showScale,
    sky = false,
    onSkip,
    className,
    children
}: HeroFrameProps): ReactNode => {
    const reduced = useReducedMotion()
    const pinned = !reduced && screens > 0
    const sectionRef = useRef<HTMLElement | null>(null)
    const stickyRef = useRef<HTMLDivElement | null>(null)
    const raRef = useRef<HTMLSpanElement | null>(null)
    const decRef = useRef<HTMLSpanElement | null>(null)
    const markerRef = useRef<HTMLSpanElement | null>(null)
    const skipRef = useRef<HTMLAnchorElement | null>(null)
    const progressRef = useRef(0)
    const listeners = useRef(new Set<ProgressListener>())
    const pinnedRef = useRef(pinned)
    const onSkipRef = useRef(onSkip)
    const [blink, setBlink] = useState<'off' | 'closing' | 'opening'>('off')
    const [skipped, setSkipped] = useState(false)

    pinnedRef.current = pinned
    onSkipRef.current = onSkip

    const setCoordinates = useCallback((value: HeroCoordinates | null) => {
        if (raRef.current) raRef.current.textContent = value ? formatHeroRa(value.ra) : 'RA --h --m'
        if (decRef.current) decRef.current.textContent = value ? formatHeroDec(value.dec) : 'DEC --°'
    }, [])

    const getProgress = useCallback(() => progressRef.current, [])

    const subscribe = useCallback((listener: ProgressListener) => {
        listeners.current.add(listener)
        listener(progressRef.current)

        return () => {
            listeners.current.delete(listener)
        }
    }, [])

    const measure = useCallback((): number => {
        const section = sectionRef.current

        if (!section || !pinnedRef.current) return 0

        const rect = section.getBoundingClientRect()
        const travel = section.offsetHeight - window.innerHeight

        return travel > 0 ? clamp01(-rect.top / travel) : 0
    }, [])

    useEffect(() => {
        let frame = 0

        const update = () => {
            frame = 0

            const progress = measure()

            progressRef.current = progress

            if (markerRef.current) markerRef.current.style.transform = `translate3d(0, ${progress * 100}%, 0)`
            if (skipRef.current) {
                const hide = pinnedRef.current && progress > 0.985
                skipRef.current.style.opacity = hide ? '0' : ''
                skipRef.current.style.pointerEvents = hide ? 'none' : ''
            }

            listeners.current.forEach(listener => listener(progress))
        }

        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', schedule, { passive: true })
        window.addEventListener('resize', schedule)

        return () => {
            cancelAnimationFrame(frame)
            window.removeEventListener('scroll', schedule)
            window.removeEventListener('resize', schedule)
        }
    }, [measure, pinned])

    useEffect(() => {
        setCoordinates(coordinates)
    }, [coordinates, setCoordinates])

    const scrollToProgress = useCallback((progress: number) => {
        const section = sectionRef.current

        if (!section || !pinnedRef.current) return

        const top = section.getBoundingClientRect().top + window.scrollY
        const travel = section.offsetHeight - window.innerHeight

        window.scrollTo({ top: top + clamp01(progress) * travel, behavior: 'smooth' })
    }, [])

    const skip = useCallback(() => {
        const section = sectionRef.current

        if (!section) return

        const land = () => {
            const top = section.getBoundingClientRect().bottom + window.scrollY

            setSkipped(true)
            onSkipRef.current?.()
            window.scrollTo({ top, behavior: 'instant' as ScrollBehavior })

            const content = document.getElementById(contentId)

            if (content) {
                if (!content.hasAttribute('tabindex')) content.setAttribute('tabindex', '-1')
                content.focus({ preventScroll: true })
            }
        }

        if (reduced) {
            land()
            return
        }

        setBlink('closing')
        window.setTimeout(() => {
            land()
            setBlink('opening')
            window.setTimeout(() => setBlink('off'), BLINK_MS * 2)
        }, BLINK_MS)
    }, [contentId, reduced])

    const value = useMemo<HeroContextValue>(() => ({
        reduced,
        pinned,
        getProgress,
        subscribe,
        setCoordinates,
        skip,
        scrollToProgress,
        skipped,
        sectionRef,
        stickyRef
    }), [reduced, pinned, getProgress, subscribe, setCoordinates, skip, scrollToProgress, skipped])

    const withScale = pinned && (showScale ?? true)

    return (
        <HeroContext.Provider value={value}>
            <section
                ref={sectionRef}
                id={id}
                aria-labelledby={titleId}
                className={cn('relative', className)}
                style={{ height: pinned ? `calc(100svh * ${1 + screens})` : '100svh' }}
            >
                <div
                    ref={stickyRef}
                    data-sky={sky ? '' : undefined}
                    className='sticky top-0 h-[100svh] overflow-hidden bg-ink'
                >
                    {children}

                    <HeroCorners />

                    <p className='label-mono pointer-events-none absolute left-5 top-[5.25rem] z-20 flex flex-wrap items-center gap-x-2 text-star-3 sm:left-8 lg:left-12'>
                        <span className='text-brass'>
                            {label}
                        </span>
                        <span aria-hidden>
                            /
                        </span>
                        <span ref={raRef} className='tabular-nums'>
                            {coordinates ? formatHeroRa(coordinates.ra) : 'RA --h --m'}
                        </span>
                        <span aria-hidden>
                            /
                        </span>
                        <span ref={decRef} className='tabular-nums'>
                            {coordinates ? formatHeroDec(coordinates.dec) : 'DEC --°'}
                        </span>
                    </p>

                    <a
                        ref={skipRef}
                        href={`#${contentId}`}
                        onClick={event => {
                            event.preventDefault()
                            skip()
                        }}
                        className='label-mono group absolute bottom-5 right-5 z-30 inline-flex items-center gap-2 py-2 text-star-3 transition-[color,opacity] duration-500 hover:text-star sm:right-8 lg:right-12'
                    >
                        <span className='h-px w-5 bg-hair-2 transition-all duration-500 group-hover:w-8 group-hover:bg-brass' />
                        Skip intro
                    </a>

                    {withScale && (
                        <div aria-hidden className='pointer-events-none absolute bottom-[22%] right-3 top-[22%] z-20 hidden w-4 sm:block lg:right-5'>
                            <span className='absolute bottom-0 right-0 top-0 w-px bg-hair' />
                            {Array.from({ length: 11 }, (_, index) => (
                                <span
                                    key={index}
                                    className={cn('absolute right-0 h-px', index % 5 === 0 ? 'w-3 bg-hair-2' : 'w-1.5 bg-hair')}
                                    style={{ top: `${index * 10}%` }}
                                />
                            ))}
                            <span ref={markerRef} className='absolute inset-x-0 top-0 block h-full'>
                                <span className='absolute -top-px right-0 block h-[3px] w-4 bg-brass' />
                            </span>
                        </div>
                    )}
                </div>
            </section>

            <div
                aria-hidden
                className='pointer-events-none fixed inset-0 z-[84] bg-ink'
                style={{
                    opacity: blink === 'closing' ? 1 : 0,
                    visibility: blink === 'off' ? 'hidden' : 'visible',
                    transition: `opacity ${blink === 'opening' ? BLINK_MS * 2 : BLINK_MS}ms cubic-bezier(0.65, 0, 0.35, 1)`
                }}
            />
        </HeroContext.Provider>
    )
}

/** Viewfinder corners: four thin L-shaped hairlines just inside the frame. */
const HeroCorners = (): ReactNode => {
    const corner = 'pointer-events-none absolute z-20 size-5 border-brass/45'

    return (
        <div aria-hidden>
            <span className={cn(corner, 'left-3 top-[4.5rem] border-l border-t lg:left-5')} />
            <span className={cn(corner, 'right-3 top-[4.5rem] border-r border-t lg:right-5')} />
            <span className={cn(corner, 'bottom-3 left-3 border-b border-l lg:left-5')} />
            <span className={cn(corner, 'bottom-3 right-3 border-b border-r lg:right-5')} />
        </div>
    )
}
